import * as BankDomain from "./bank-domain"
import * as Model from "../../model"
import * as Tabledefinition from "./../../component/scenarii/detail/table-definition"

export function countRequiredValidationLoans(loansWithDecomposition: Model.LoanWithDecomposition[]): number {
    let count = 0
    loansWithDecomposition.forEach(loanWithDecomp => {
        const loan = loanWithDecomp.loan
        if (loan.simulate && requireValidation(loanWithDecomp)) count += 1
    })

    return count ? count : null
}

export function distinctBanks(loans: Model.Loan[]): Model.Bank[] {
    return loans.reduce<Model.Bank[]>((banks, loan) => {
        if (!BankDomain.contains(banks, loan.bank)) banks.push(loan.bank)
        return banks
    }, [])
}
export function calculateNetCashFlow(type, year, localAuthority, operations, scenario, loans) {
    let result = 0
    if (loans) {
        let loan = loans.find(l => l.year == year)
        if (loan) {
            result = loan.reimbours
        }
    }

    result = Tabledefinition._aggregateCAFBrute(type, year, localAuthority, operations, null, [], loans) - result

    return result || null
}
/**
 * Retourne la liste des emprunts ayant été contractés, soit les emprunts qui ne
 * sont pas des simulations (`loan.simulate === false`).
 * @param loans
 */
export function filterContractedLoans(loans: Model.Loan[]): Model.Loan[] {
    return loans.filter(loan => loan && !loan.simulate)
}

/**
 * Retourne la liste des emprunts (avec décompositions) ayant été contractés,
 * soit les emprunts qui ne sont pas des simulations
 * (`loanWithDecomposition.loan.simulate === false`).
 * @param loansWithDecomposition
 */
export function filterContractedLoansWithDecomposition(
    loansWithDecomposition: Model.LoanWithDecomposition[]
): Model.LoanWithDecomposition[] {
    return loansWithDecomposition.filter(l => l && l.loan && !l.loan.simulate)
}

/**
 * Retourne la première date de paiement parmi la liste des decompositions
 * d'emprunts.
 * @param loanDecompositions
 */
export function findFirstPaymentDate(
    loanDecompositions: Model.LoanDecomposition[]
): Date {
    return loanDecompositions.reduce<Date>((date, d) => {
        const firstDate = d.paymentDates[0]
        if (!date || firstDate.getTime() < date.getTime()) return firstDate
        return date
    }, null)
}

/**
 * Retourne la dernière date de paiement parmi la liste des decompositions
 * d'emprunts.
 * @param loanDecompositions
 */
export function findLastPaymentDate(
    loanDecompositions: Model.LoanDecomposition[]
): Date {
    return loanDecompositions.reduce<Date>((date, d) => {
        const lastDate = d.paymentDates[d.paymentDates.length - 1]
        if (!date || (lastDate && lastDate.getTime() > date.getTime())) return lastDate
        return date
    }, null)
}

export function getAnnuity(
    decomposition: Model.LoanDecomposition,
    year: number
): number {
    return decomposition.paymentDates.reduce((prevAmount, date, i) => {
        const paymentDateYear = date.getFullYear()
        if (paymentDateYear > year) return prevAmount
        const paymentAmount = paymentDateYear === year
            ? decomposition.paymentAmounts[i] ? decomposition.paymentAmounts[i] : 0
            : 0
        return null !== prevAmount ? prevAmount + paymentAmount : paymentAmount
    }, null as number)
}

export function getAnnuitySemestier(
    decomposition: Model.LoanDecomposition,
    year: number
): number {
    return decomposition.paymentDates.reduce((prevAmount, date, i) => {
        const paymentDateYear = date.getFullYear()
        if (paymentDateYear > year) return prevAmount
        const paymentAmount = paymentDateYear === year
            ? decomposition.paymentAmounts[i - 1] ? decomposition.paymentAmounts[i - 1] : 0
            : 0
        return null !== prevAmount ? prevAmount + paymentAmount : paymentAmount
    }, null as number)
}

export function getAverageRate(
    loans: Model.LoanWithDecomposition[],
    year: number
): number {
    let totalInterest: number = 0
    let totalCRD: number = 0

    loans.forEach(loanWithDecomp => {
        totalInterest += getYearInterest(loanWithDecomp.decomposition, year)
        totalCRD += getYearRemainingCapital(loanWithDecomp.decomposition, year)
    })
    const averageRate: number = totalInterest / totalCRD
    return averageRate ? averageRate : 0
}

export function getAverageRatePerFixed(
    loans: Model.LoanWithDecomposition[],
    year: number
): number {
    let totalInterest: number = 0
    let totalCRD: number = 0

    loans.forEach(loanWithDecomp => {
        if (loanWithDecomp.loan.variableRate) return
        totalInterest += getYearInterest(loanWithDecomp.decomposition, year)
        totalCRD += getYearRemainingCapital(loanWithDecomp.decomposition, year)
    })

    const averageRatePerFixed: number = totalInterest / totalCRD
    return averageRatePerFixed ? averageRatePerFixed : 0
}

export function getAverageRatePerVariable(
    loans: Model.LoanWithDecomposition[],
    year: number
): number {
    let totalInterest: number = 0
    let totalCRD: number = 0

    loans.forEach(loanWithDecomp => {
        if (!loanWithDecomp.loan.variableRate) return
        totalInterest += getYearInterest(loanWithDecomp.decomposition, year)
        totalCRD += getYearRemainingCapital(loanWithDecomp.decomposition, year)
    })

    const averageRatePerVariable: number = totalInterest / totalCRD
    return averageRatePerVariable ? averageRatePerVariable : 0
}

export function getConstantAmortizationInterest(
    loan: Model.Loan,
    decomposition: Model.LoanDecomposition,
    paymentDateIndex: number
): number {
    const interestVariation = loan.interestVariations.find(iv => iv.paymentDateIndex === paymentDateIndex)
    return interestVariation
        ? interestVariation.interest
        : decomposition.periodicRates[paymentDateIndex] * decomposition.remainingCapitals[paymentDateIndex]
}

export function getConstantAmortizationPaymentAmount(
    decomposition: Model.LoanDecomposition,
    paymentDateIndex: number
): number {
    return decomposition.reimboursedCapitals[paymentDateIndex] + decomposition.interests[paymentDateIndex]
}

export function getConstantAmortizationReimboursedCapital(
    loan: Model.Loan,
    paymentDateIndex: number
): number {
    const reimboursedCapitalVariation = getReimboursedCapitalVariation(loan, paymentDateIndex)

    return reimboursedCapitalVariation
        ? reimboursedCapitalVariation.reimboursedCapital
        : loan.amount / (loan.duration / 12 * loan.periodicity)
}

export function getConstantAnnuityInterest(
    loan: Model.Loan,
    decomposition: Model.LoanDecomposition,
    paymentDateIndex: number
): number {
    const remainingCapital = 0 === paymentDateIndex
        ? loan.amount
        : decomposition.remainingCapitals[paymentDateIndex]

    const interestVariation = loan.interestVariations.find(iv => iv.paymentDateIndex === paymentDateIndex)
    return interestVariation
        ? interestVariation.interest
        : decomposition.periodicRates[paymentDateIndex] * remainingCapital
}

export function getConstantAnnuityInteresttwo(
    loan: Model.Loan,
    decomposition: Model.LoanDecomposition,
    paymentDateIndex: number
): number {
    const remainingCapital = decomposition.remainingCapitals[paymentDateIndex]
    const interestVariation = loan.interestVariations.find(iv => iv.paymentDateIndex === paymentDateIndex)
    return interestVariation
        ? interestVariation.interest
        : decomposition.periodicRates[paymentDateIndex] * remainingCapital
}

export function getConstantAnnuityPaymentAmount(
    loan: Model.Loan,
    decomposition: Model.LoanDecomposition,
    paymentDateIndex: number
): number {
    const interestVariation = getInterestVariation(loan, paymentDateIndex)
    const reimboursedCapitalVariation = getReimboursedCapitalVariation(loan, paymentDateIndex)

    if (null !== interestVariation && null !== reimboursedCapitalVariation)
        return interestVariation.interest + reimboursedCapitalVariation.reimboursedCapital

    return loan.amount * decomposition.periodicRates[paymentDateIndex] / (1 - (1 + decomposition.periodicRates[paymentDateIndex]) ** (- loan.duration * loan.periodicity / 12))
}

export function getConstantAnnuityReimboursedCapital(
    loan: Model.Loan,
    decomposition: Model.LoanDecomposition,
    paymentDateIndex: number
): number {
    const reimboursedCapitalVariation = getReimboursedCapitalVariation(loan, paymentDateIndex)
    return reimboursedCapitalVariation
        ? reimboursedCapitalVariation.reimboursedCapital
        : decomposition.paymentAmounts[paymentDateIndex] - decomposition.interests[paymentDateIndex]
}

export function getDecomposition(loan: Model.Loan): Model.LoanDecomposition {
    const decomposition: Model.LoanDecomposition = {
        interests: [],
        paymentAmounts: [],
        paymentDates: getPaymentDates(loan),
        periodicRates: [],
        reimboursedCapitals: [],
        remainingCapitals: []
    }

    for (let i = 0, length = decomposition.paymentDates.length; i < length; i++) {
        if (loan.constantAmortization) {
            const reimboursedCapital = getConstantAmortizationReimboursedCapital(loan, i)
            decomposition.reimboursedCapitals.push(reimboursedCapital)
            decomposition.remainingCapitals.push(getRemainingCapital(loan, decomposition, i))
            decomposition.periodicRates.push(getPeriodicRate(loan, i))
            decomposition.interests.push(getConstantAmortizationInterest(loan, decomposition, i))
            const paymentAmount = getConstantAmortizationPaymentAmount(decomposition, i)
            decomposition.paymentAmounts.push(paymentAmount)
        } else {
            decomposition.periodicRates.push(getPeriodicRate(loan, i))
            const paymentAmount = getConstantAnnuityPaymentAmount(loan, decomposition, i)
            decomposition.remainingCapitals.push(getRemainingCapital(loan, decomposition, i))
            decomposition.paymentAmounts.push(paymentAmount)
            decomposition.interests.push(getConstantAnnuityInterest(loan, decomposition, i))
            const reimboursedCapital = getConstantAnnuityReimboursedCapital(loan, decomposition, i)
            decomposition.reimboursedCapitals.push(reimboursedCapital)
        }
    }

    roundDecomposition(decomposition)

    return decomposition
}

export function getDecompositionall(loan: Model.Loan): Model.LoanDecomposition {
    const decomposition: Model.LoanDecomposition = {
        interests: [],
        paymentAmounts: [],
        paymentDates: getPaymentDates(loan),
        periodicRates: [],
        reimboursedCapitals: [],
        remainingCapitals: []
    }

    for (let i = 0, length = decomposition.paymentDates.length; i < length; i++) {
        if (loan.constantAmortization) {
            const reimboursedCapital = getConstantAmortizationReimboursedCapital(loan, i)
            decomposition.reimboursedCapitals.push(reimboursedCapital)
            decomposition.remainingCapitals.push(getRemainingCapital(loan, decomposition, i))
            decomposition.periodicRates.push(getPeriodicRate(loan, i))
            decomposition.interests.push(getConstantAmortizationInterest(loan, decomposition, i))
            const paymentAmount = getConstantAmortizationPaymentAmount(decomposition, i)
            decomposition.paymentAmounts.push(paymentAmount)
        } else {
            decomposition.periodicRates.push(getPeriodicRate(loan, i))
            const paymentAmount = getConstantAnnuityPaymentAmount(loan, decomposition, i)
            decomposition.remainingCapitals.push(getRemainingCapital(loan, decomposition, i))
            decomposition.paymentAmounts.push(paymentAmount)
            decomposition.interests.push(getConstantAnnuityInteresttwo(loan, decomposition, i))
            const reimboursedCapital = getConstantAnnuityReimboursedCapital(loan, decomposition, i)
            decomposition.reimboursedCapitals.push(reimboursedCapital)

        }
    }
    if (!loan.constantAmortization && decomposition.paymentDates.length == decomposition.remainingCapitals.length) {
        decomposition.interests.splice(0, decomposition.interests.length)
        decomposition.reimboursedCapitals.splice(0, decomposition.reimboursedCapitals.length)
        for (let i = 0, length = decomposition.paymentDates.length; i < length; i++) {
            decomposition.interests.push(getConstantAnnuityInteresttwo(loan, decomposition, i))
            const reimboursedCapital = getConstantAnnuityReimboursedCapital(loan, decomposition, i)
            decomposition.reimboursedCapitals.push(reimboursedCapital)

        }
    }
    roundDecomposition(decomposition)
    return decomposition
}

export function getDecompositions(loans: Model.Loan[]): Model.LoanDecomposition[] {
    return loans.map(loan => getDecomposition(loan))
}

export function getEmptyLoan(localAuthority: Model.LocalAuthority): Model.Loan {
    return {
        amount: null,
        bank: null,
        constantAmortization: true,
        contractedOn: new Date(),
        duration: null,
        id: null,
        interestVariations: [],
        localAuthority,
        number: null,
        periodicity: 1,
        rate: null,
        rateVariations: [],
        reimboursedCapitalVariations: [],
        simulate: false,
        subject: null,
        variableRate: false,
        detteRecuperable: false
    }
}

export function getInterestVariation(
    loan: Model.Loan,
    paymentDateIndex: number
): Model.InterestVariation | null {
    return loan.interestVariations.find(iv =>
        paymentDateIndex === iv.paymentDateIndex
    ) || null
}

export function getInterestVariationIndex(
    loan: Model.Loan,
    interestVariation: Model.InterestVariation
): number {
    if (!interestVariation) return null
    return loan.interestVariations.findIndex(iv =>
        interestVariation.paymentDateIndex === iv.paymentDateIndex
    )
}

export function getLastRateVariation(
    loan: Model.Loan,
    paymentDateIndex: number
): Model.RateVariation {
    const prevRateVariations = loan.rateVariations.filter(rv => rv.paymentDateIndex <= paymentDateIndex)
    return prevRateVariations.reduce((prevRv, rv) =>
        !prevRv || rv.paymentDateIndex > prevRv.paymentDateIndex ? rv : prevRv
        , null)
}

export function getLoansWithDecomposition(loans: Model.Loan[]): Model.LoanWithDecomposition[] {
    return loans.map(loan => ({
        decomposition: getDecomposition(loan),
        loan
    }))
}

export function geteDcompositionWithLoans(loans: Model.Loan[]): Model.LoanWithDecomposition[] {
    return loans.map(loan => ({
        decomposition: getDecompositionall(loan),
        loan
    }))
}

export function getFilteredLoans(loans: Model.Loan[]): LoansFiltered {
    const loansFiltered = {
        simulated: [] as Model.Loan[],
        normal: [] as Model.Loan[]
    }

    loans.forEach(loan => {
        loan.simulate ? loansFiltered.simulated.push(loan) : loansFiltered.normal.push(loan)
    })

    return loansFiltered
}

export function getNumberOfPaymentDates(loan: Model.Loan): number {
    return Math.ceil(loan.duration * loan.periodicity / 12)
}

export function getPaymentDates(loan: Model.Loan): Date[] {
    const period = getPeriod(loan)
    const paymentDates: Date[] = new Array(getNumberOfPaymentDates(loan))
    for (let i = 0; i < paymentDates.length; i++) {
        const paymentDate = new Date(loan.contractedOn)

        paymentDate.setMonth(paymentDate.getMonth() + period * (i + 1))

        paymentDates[i] = paymentDate
    }

    return paymentDates
}

export function getPaymentDatescheck(loan: Model.Loan): Date[] {
    const period = getPeriod(loan)
    const paymentDates: Date[] = new Array(getNumberOfPaymentDates(loan))
    for (let i = 0; i < paymentDates.length; i++) {
        const paymentDate = new Date(loan.contractedOn)
        paymentDate.setMonth(paymentDate.getMonth() + period * i)
        paymentDates[i] = paymentDate
    }
    return paymentDates
}


export function getPeriod(loan: Model.Loan) {
    return 12 / loan.periodicity
}

export function getPeriodicRate(loan: Model.Loan, paymentDateIndex: number): number {
    const lastRateVariation = getLastRateVariation(loan, paymentDateIndex)
    const appliedRate = !loan.variableRate || !loan.rateVariations.length || !lastRateVariation
        ? loan.rate
        : lastRateVariation.rate

    return appliedRate / loan.periodicity
}

export function getPreviousReimboursedCapital(
    decomposition: Model.LoanDecomposition,
    paymentDateIndex: number
): number {
    return paymentDateIndex > 0
        ? decomposition.reimboursedCapitals[paymentDateIndex - 1]
        : 0
}

export function getPreviousRemainingCapital(
    loan: Model.Loan,
    decomposition: Model.LoanDecomposition,
    paymentDateIndex: number
): number {
    // HERE...
    return paymentDateIndex > 0
        ? decomposition.remainingCapitals[paymentDateIndex - 1]
        : loan.amount
}

export function getRateVariation(
    loan: Model.Loan,
    paymentDateIndex: number
): Model.RateVariation | null {
    return loan.rateVariations.find(rv =>
        paymentDateIndex === rv.paymentDateIndex
    ) || null
}

export function getRateVariationIndex(
    loan: Model.Loan,
    rateVariation: Model.RateVariation
): number {
    return loan.rateVariations.findIndex(rv =>
        rateVariation.paymentDateIndex === rv.paymentDateIndex
    )
}

export function getReimboursedCapitalVariation(
    loan: Model.Loan,
    paymentDateIndex: number
): Model.ReimboursedCapitalVariation | null {
    return loan.reimboursedCapitalVariations.find(rcv =>
        paymentDateIndex === rcv.paymentDateIndex
    ) || null
}

export function getReimboursedCapitalVariationIndex(
    loan: Model.Loan,
    reimboursedCapitalVariation: Model.ReimboursedCapitalVariation
): number {
    return loan.reimboursedCapitalVariations.findIndex(rcv =>
        reimboursedCapitalVariation.paymentDateIndex === rcv.paymentDateIndex
    )
}

export function getRemainingCapital(
    loan: Model.Loan,
    decomposition: Model.LoanDecomposition,
    paymentDateIndex: number
): number {
    if (paymentDateIndex === 0) {
        return loan.amount
    }

    const previousRemainingCapital = getPreviousRemainingCapital(loan, decomposition, paymentDateIndex)
    return previousRemainingCapital - decomposition.reimboursedCapitals[paymentDateIndex - 1]
}

export function getTotalInterestByYear(
    loans: Model.Loan[],
    year: number
): number {
    const loansWithDecomposition = geteDcompositionWithLoans(loans)
    let total = 0
    loansWithDecomposition.forEach(loanWithDecomp => {
        const decomposition = loanWithDecomp.decomposition
        total += getYearInterest(decomposition, year)
    })
    return total
}

export interface SimulatedLoanTotals {
    interest: number
    remainingCapital: number
    reimboursedCapital: number
}

export interface SimulatedLoanTotalsByYear {
    [year: number]: SimulatedLoanTotals
}

export function getEmptySimulatedLoanTotals(): SimulatedLoanTotals {
    return { interest: 0, remainingCapital: 0, reimboursedCapital: 0 }
}

/**
 * Ne conserve que les échéances dont le capital restant dû est strictement positif.
 * Reproduit le filtrage `decom` de component/debt/debt-decomposition/index.tsx
 */
export function filterPositiveRemainingCapitals(
    decomposition: Model.LoanDecomposition
): Model.LoanDecomposition {
    const filtered: Model.LoanDecomposition = {
        interests: [],
        paymentAmounts: [],
        paymentDates: [],
        periodicRates: [],
        reimboursedCapitals: [],
        remainingCapitals: []
    }

    decomposition.paymentDates.forEach((date, i) => {
        if (!(decomposition.remainingCapitals[i] > 0)) return

        filtered.interests.push(decomposition.interests[i])
        filtered.paymentAmounts.push(decomposition.paymentAmounts[i])
        filtered.paymentDates.push(date)
        filtered.periodicRates.push(decomposition.periodicRates[i])
        filtered.reimboursedCapitals.push(decomposition.reimboursedCapitals[i])
        filtered.remainingCapitals.push(decomposition.remainingCapitals[i])
    })

    return filtered
}

/**
 * Totaux annuels des emprunts simulés, reproduisant la ligne « Total dette simulée »
 * des onglets de la dette détaillée. Périmètre : tous les emprunts `simulate`,
 * `detteRecuperable` inclus.
 *
 * Chaque grandeur reprend la variante de décomposition de son onglet
 * (cf. component/debt/debt-component.tsx) et son filtrage
 * (cf. component/debt/debt-decomposition/index.tsx) :
 *  - interest           → onglet « Intérêt »           : getDecompositionall, non filtrée
 *  - remainingCapital   → onglet « Capital restant »   : getDecompositionall, filtrée
 *  - reimboursedCapital → onglet « Capital remboursé » : getDecomposition,    filtrée
 */
export function getSimulatedLoansTotalsByYear(
    loans: Model.Loan[],
    years: number[]
): SimulatedLoanTotalsByYear {
    const totalsByYear: SimulatedLoanTotalsByYear = {}
    if (!years || !years.length) return totalsByYear

    years.forEach(year => { totalsByYear[year] = getEmptySimulatedLoanTotals() })

    if (!loans || !loans.length) return totalsByYear

    loans.forEach(loan => {
        if (!loan || !loan.simulate) return

        const decompositionAll = getDecompositionall(loan)
        const remainingDecom = filterPositiveRemainingCapitals(decompositionAll)
        const reimboursedDecom = filterPositiveRemainingCapitals(getDecomposition(loan))

        years.forEach(year => {
            const totals = totalsByYear[year]
            // Les helpers getYear* renvoient null quand aucune échéance ne concerne
            // l'année : `+= null` vaut `+= 0`, comme dans debt-decomposition/index.tsx
            totals.interest += getYearInterest(decompositionAll, year)
            totals.remainingCapital += getYearRemainingCapital(remainingDecom, year)
            totals.reimboursedCapital += getYearReimboursedCapital(reimboursedDecom, year)
        })
    })

    return totalsByYear
}

/** Variante pour les appelants qui ne disposent que des emprunts décomposés. */
export function getSimulatedLoansTotalsByYearFromDecomposed(
    loansWithDecomposition: Model.LoanWithDecomposition[],
    years: number[]
): SimulatedLoanTotalsByYear {
    return getSimulatedLoansTotalsByYear(
        (loansWithDecomposition || []).map(lwd => lwd.loan),
        years
    )
}

/** Accès sûr : zéros si l'année n'a pas été calculée. */
export function getSimulatedLoanTotals(
    totalsByYear: SimulatedLoanTotalsByYear,
    year: number
): SimulatedLoanTotals {
    const totals = totalsByYear ? totalsByYear[year] : null
    return totals ? totals : getEmptySimulatedLoanTotals()
}

export function getYearInterest(
    decomposition: Model.LoanDecomposition,
    year: number
): number {
    return decomposition.paymentDates.reduce((prevAmount, date, i) => {
        const paymentDateYear = date.getFullYear()
        if (paymentDateYear > year) return prevAmount
        const interest = paymentDateYear === year
            ? decomposition.interests[i] ? decomposition.interests[i] : 0
            : 0
        return null !== prevAmount ? prevAmount + interest : interest
    }, null as number)
}

export function getYearReimboursedCapital(
    decomposition: Model.LoanDecomposition,
    year: number
): number {
    return decomposition.paymentDates.reduce((prevAmount, date, i) => {
        const paymentDateYear = date.getFullYear()
        if (paymentDateYear > year) return prevAmount
        const reimboursed = paymentDateYear === year
            ? decomposition.reimboursedCapitals[i] ? decomposition.reimboursedCapitals[i] : 0
            : 0
        return null !== prevAmount ? prevAmount + reimboursed : reimboursed
    }, null as number)
}

export function getYearRemainingCapital(
    decomposition: Model.LoanDecomposition,
    year: number
): number {
    const i = decomposition.paymentDates.findIndex(date => date.getFullYear() === year);
    return i !== -1 ? decomposition.remainingCapitals[i] : null;

    /*let retnt = []
    let value = 0
    decomposition.paymentDates.reduce((prevAmount, date, i) => {
        const paymentDateYear = date.getFullYear()
        if (paymentDateYear < year || paymentDateYear > year) return prevAmount
        paymentDateYear === year ? retnt.push(decomposition.remainingCapitals[i])
            : 0
        value = paymentDateYear === year ? decomposition.reimboursedCapitals[i] + value
            : 0
    }, null as number)
    let last: number = retnt[0];
    let lastvalue = last - value
    return lastvalue && lastvalue > 0 ? lastvalue : null*/
}

export function getTotalAnnuity(
    decompositions: Model.LoanDecomposition[],
    year: number
): number {
    return decompositions.reduce<number>((totalAnnuity, decomposition) => {
        const annuity = getAnnuity(decomposition, year)
        return totalAnnuity ? totalAnnuity + annuity : annuity
    }, null)
}

export function getTotalNominal(
    loans: Model.Loan[],
    year: number
): number | null {
    return loans.reduce<number>((totalNominal, loan) => {
        if (loan.contractedOn.getFullYear() !== year) return totalNominal
        return totalNominal ? totalNominal + loan.amount : loan.amount
    }, null)
}

export function getTotalYearInterest(
    decompositions: Model.LoanDecomposition[],
    year: number
): number {
    return decompositions.reduce<number>((totalInterest, decomposition) => {
        const interest = getYearInterest(decomposition, year)
        return totalInterest ? totalInterest + interest : interest
    }, null)
}

export function getTotalYearReimbursedCapital(
    decompositions: Model.LoanDecomposition[],
    year: number
): number {
    return decompositions.reduce<number>(
        (totalReimbursedCapital, decomposition) => {
            const reimbursedCapital = getYearReimboursedCapital(decomposition, year)
            return totalReimbursedCapital
                ? totalReimbursedCapital + reimbursedCapital
                : reimbursedCapital
        },
        null
    )
}
export function getYearReimbursedCapital(
    loans: any,
    year: number
): number {
    let result = 0
    if (loans) {
        let loan = loans.find(l => l.year == year)
        if (loan) {
            result = Math.round(loan.reimbours)
        }
    }
    return result || null
}
export function getTotalYearRemainingCapital(
    decompositions: Model.LoanDecomposition[],
    year: number
): number {
    return decompositions.reduce<number>(
        (totalRemainingCapital, decomposition) => {
            const remainingCapital = getYearRemainingCapital(decomposition, year)
            return totalRemainingCapital
                ? totalRemainingCapital + remainingCapital
                : remainingCapital
        },
        null
    )
}

export function requireValidation(loanWithDecomp: Model.LoanWithDecomposition): boolean {
    const date = loanWithDecomp.decomposition.paymentDates[0]
    const simulate = loanWithDecomp.loan.simulate

    if (date.getFullYear() <= new Date().getFullYear() - 1 && simulate) {
        return true
    } else return false
}

export function roundDecomposition(decomposition: Model.LoanDecomposition): void {
    decomposition.interests = decomposition.interests.map(i => Math.round(i * 100) / 100)
    decomposition.paymentAmounts = decomposition.paymentAmounts.map(pa => Math.round(pa * 100) / 100)
    decomposition.periodicRates = decomposition.periodicRates.map(pr => Math.round(pr * 10000) / 10000)
    decomposition.reimboursedCapitals = decomposition.reimboursedCapitals.map(rc => Math.round(rc * 100) / 100)
    decomposition.remainingCapitals = decomposition.remainingCapitals.map(rc => Math.round(rc * 100) / 100)
}

interface LoansFiltered {
    simulated: Model.Loan[],
    normal: Model.Loan[]
}



// WEBPACK FOOTER //
// ./src/domain/sfp/loan-domain.ts